import { Link } from "react-router-dom";
import { Lock, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { TRIAL_READ_ONLY_MESSAGE, useTrialMode } from "@/hooks/useTrialMode";

export default function TrialModeBanner() {
  const { toast } = useToast();
  const trial = useTrialMode();

  if (!trial.isReadOnly || trial.reminderDismissed) return null;

  const handleDismiss = () => {
    trial.dismissReminder.mutate(undefined, {
      onSuccess: () => {
        toast({
          title: "Reminder hidden",
          description: "The workspace stays read-only until you choose a plan.",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Could not hide reminder",
          description: error?.message ?? "Please try again.",
          variant: "destructive",
        });
      },
    });
  };

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-amber-950">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-100">
            <Lock className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium">Your free account has used its 10 included articles.</p>
            <p className="text-sm text-amber-900">
              Subscribe to a package to unlock real data creation, publishing, integrations, and automation.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild size="sm" variant="outline" className="border-amber-300 bg-white text-amber-950 hover:bg-amber-100">
            <Link to="/billing/plans">View Packages</Link>
          </Button>
          <Button asChild size="sm" className="bg-amber-600 text-white hover:bg-amber-700">
            <Link to="/billing/plans">
              <Sparkles className="mr-2 h-4 w-4" />
              Start Subscription
            </Link>
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-amber-950 hover:bg-amber-100"
            onClick={handleDismiss}
            disabled={trial.dismissReminder.isPending}
            title={TRIAL_READ_ONLY_MESSAGE}
          >
            <X className="mr-2 h-4 w-4" />
            Don't Show Again
          </Button>
        </div>
      </div>
    </div>
  );
}
